'use client';
/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { nanoid } from 'nanoid';
import type { ArchiMateDiagram, ArchiMateElement, ArchiMateRelationship, Layer, ElementType } from '@/lib/archimate/types';
import { ARCHIMATE_ELEMENTS, getElementsByLayer, getElementMetadata } from '@/lib/archimate/elements';
import { elementContains, isStructuralRelationship } from '@/lib/archimate/layout';

// Lazy load D3-based canvas component
const ArchiMateCanvas = dynamic(
  () => import('@/components/archimate/ArchiMateCanvas').then(mod => ({ default: mod.ArchiMateCanvas })),
  {
    ssr: false,
    loading: () => <div className="flex items-center justify-center h-full text-foreground-tertiary">Loading canvas...</div>,
  }
);
import { PaletteIcon } from '@/components/archimate/PaletteIcon';
import { ContextMenu } from '@/components/archimate/ContextMenu';
import { getValidRelationships } from '@/lib/archimate/validation';
import type { RelationshipType } from '@/lib/archimate/types';
import { exportToPNG, downloadXML } from '@/lib/archimate/export';
import { DiagramBrowser } from '@/components/archimate/DiagramBrowser';
import { ComponentLibrary } from '@/components/archimate/ComponentLibrary';
import { InspectModal } from '@/components/archimate/InspectModal';
import { WorkspaceSelector } from '@/components/archimate/WorkspaceSelector';
import { WorkspaceManager } from '@/components/archimate/WorkspaceManager';
import { saveDiagram, loadDiagram, getAllDiagrams } from '@/lib/archimate/storage/db';
import ADRPanel from '@/components/archimate/ADRPanel';
import CostPanel from '@/components/archimate/CostPanel';
import SecurityPanel from '@/components/archimate/SecurityPanel';
import DebtPanel from '@/components/archimate/DebtPanel';
import ValidationPanel from '@/components/archimate/ValidationPanel';
import PerformancePanel from '@/components/archimate/PerformancePanel';
import TeamPanel from '@/components/archimate/TeamPanel';
import ChangelogPanel from '@/components/archimate/ChangelogPanel';
import ScenarioPanel from '@/components/archimate/ScenarioPanel';
import MigrationPanel from '@/components/archimate/MigrationPanel';
import StakeholderPanel from '@/components/archimate/StakeholderPanel';
import QueryBar from '@/components/archimate/QueryBar';
import InfrastructurePanel from '@/components/archimate/InfrastructurePanel';
import MiniMap from '@/components/archimate/MiniMap';
import ViewGeneratorPanel from '@/components/archimate/ViewGeneratorPanel';
import SequenceDiagramPanel from '@/components/archimate/SequenceDiagramPanel';
import type { ExtendedArchiMateDiagram } from '@/lib/archimate/extended-types';
import { ensureExtendedDiagram } from '@/lib/archimate/diagram-utils';
import { trackDiagramChange } from '@/lib/archimate/auto-tracking';
import LinkToEntityModal from '@/components/command-center/LinkToEntityModal';

type CapabilityModelLayout = 'capability-grid' | 'value-stream' | 'platform-stack';

interface CapabilityChild {
  id?: string;
  name: string;
  description: string;
  maturity?: string;
  owner?: string;
  priority?: string;
}

interface CapabilityNode extends CapabilityChild {
  category?: string;
  score?: number;
  dependencies?: string[];
  children?: CapabilityChild[];
}

interface CapabilityModelResponse {
  summary: string;
  assumptions?: string[];
  capabilities: CapabilityNode[];
}

export default function ArchiMateEditorPage() {
  const [mounted, setMounted] = useState(false);
  const [diagram, _setDiagram] = useState<ExtendedArchiMateDiagram>(() =>
    ensureExtendedDiagram({
      id: nanoid(),
      name: 'Untitled Diagram',
      elements: [],
      relationships: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    })
  );

  // Wrapper for setDiagram with automatic change tracking
  const setDiagram = (
    value: ExtendedArchiMateDiagram | ((prev: ExtendedArchiMateDiagram) => ExtendedArchiMateDiagram)
  ) => {
    _setDiagram((prevDiagram) => {
      const newDiagram = typeof value === 'function' ? value(prevDiagram) : value;

      // Skip tracking if this is a new/loaded diagram (id changed) or if diagrams are identical
      if (newDiagram.id !== prevDiagram.id || newDiagram === prevDiagram) {
        return newDiagram;
      }

      // Apply automatic change tracking
      return trackDiagramChange(prevDiagram, newDiagram, { autoTrack: true });
    });
  };

const CAPABILITY_SCENARIOS: { value: CapabilityModelLayout; label: string; description: string }[] = [
  {
    value: 'capability-grid',
    label: 'Capability Grid',
    description: 'Organize capabilities by category columns (strategy, customer, operations, platform, enablers).',
  },
  {
    value: 'value-stream',
    label: 'Value Stream Journey',
    description: 'Lay out capabilities along a left-to-right journey with supporting capabilities underneath.',
  },
  {
    value: 'platform-stack',
    label: 'Platform Stack',
    description: 'Stack capabilities in tiers (customer experience, core, platform, enablers) to visualize layering.',
  },
];

const [selectedLayer, setSelectedLayer] = useState<Layer>('application');
  const [selectedElement, setSelectedElement] = useState<ArchiMateElement | null>(null);
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [tool, setTool] = useState<'select' | 'connect'>('select');
  const [pendingConnection, setPendingConnection] = useState<string | null>(null);
  const [showRelationshipDialog, setShowRelationshipDialog] = useState(false);
  const [relationshipData, setRelationshipData] = useState<{
    sourceId: string;
    targetId: string;
    validTypes: RelationshipType[];
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    type: 'element' | 'relationship';
    id: string;
  } | null>(null);
  const [editingElement, setEditingElement] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showBrowser, setShowBrowser] = useState(false);
  const [showComponentLibrary, setShowComponentLibrary] = useState(false);
  const [componentLibraryDocked, setComponentLibraryDocked] = useState(false);
  const [showWorkspaceManager, setShowWorkspaceManager] = useState(false);
  const [clipboard, setClipboard] = useState<ArchiMateElement | null>(null);
  const [showCapabilityGenerator, setShowCapabilityGenerator] = useState(false);
  const [capabilityForm, setCapabilityForm] = useState({
    industry: 'technology',
    strategicIntent: 'Scale a modern digital platform across regions',
    focusAreas: 'customer experience, operational excellence, platform enablement',
    maturityTarget: 'Target 12-18 month capability roadmap.',
    includeDependencies: true,
    modelType: 'capability-grid' as CapabilityModelLayout,
  });
  const [capabilityLoading, setCapabilityLoading] = useState(false);
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const [lastCapabilitySummary, setLastCapabilitySummary] = useState<string | null>(null);
  const [capabilityDraft, setCapabilityDraft] = useState<{
    elements: ArchiMateElement[];
    relationships: ArchiMateRelationship[];
    summary: string;
    type: CapabilityModelLayout;
  } | null>(null);
  const [inspectElement, setInspectElement] = useState<ArchiMateElement | null>(null);
  const [linkToCommandCenterElement, setLinkToCommandCenterElement] = useState<ArchiMateElement | null>(null);
  const containmentStateRef = useRef<Map<string, string | null>>(new Map());

  // Extended feature panels state
  const [openPanel, setOpenPanel] = useState<string | null>(null);
  const [queryBarOpen, setQueryBarOpen] = useState(false);
  const [queryResults, setQueryResults] = useState<ArchiMateElement[]>([]);
  const [minimapVisible, setMinimapVisible] = useState(true);
  const [viewport, setViewport] = useState({ x: 0, y: 0, width: 800, height: 600, zoom: 1 });
  const [activeHeatmap, setActiveHeatmap] = useState<'cost' | 'security' | 'debt' | 'performance' | null>(null);

  const handleExportPNG = async () => {
    const svg = document.querySelector('#archimate-canvas') as SVGSVGElement;
    if (svg) {
      await exportToPNG(svg, diagram.name);
    }
  };

  const handleExportXML = () => {
    downloadXML(diagram);
  };

  const handleSaveDiagram = async () => {
    await saveDiagram(diagram);
  };

  const handleLoadDiagram = async (id: string) => {
    const loaded = await loadDiagram(id);
    if (loaded) {
      setDiagram(loaded);
      setSelectedElement(null);
      setSelectedElementIds([]);
      setCapabilityDraft(null);
    }
  };

  const handleRenameDiagram = async (id: string, newName: string) => {
    const diagramToRename = await loadDiagram(id);
    if (diagramToRename) {
      const updated = { ...diagramToRename, name: newName, updatedAt: Date.now() };
      await saveDiagram(updated);

      // If this is the current diagram, update state
      if (diagram.id === id) {
        setDiagram(updated);
      }
    }
  };

  const handleDeleteDiagram = async (id: string) => {
    if (!confirm('Are you sure you want to delete this diagram? This action cannot be undone.')) {
      return;
    }

    const { deleteDiagram } = await import('@/lib/archimate/storage/db');
    await deleteDiagram(id);

    // If this is the current diagram, create a new one
    if (diagram.id === id) {
      handleNewDiagram();
    }
  };

  const handleNewDiagram = () => {
    const newDiagram = ensureExtendedDiagram({
      id: nanoid(),
      name: 'Untitled Diagram',
      elements: [],
      relationships: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    setDiagram(newDiagram);
    setSelectedElement(null);
    setSelectedElementIds([]);
    setCapabilityDraft(null);
    setShowBrowser(false);
  };

  const promptContainmentRelationship = (
    parentId: string,
    childId: string,
    diagramState: ExtendedArchiMateDiagram
  ) => {
    const parentElement = diagramState.elements.find((el) => el.id === parentId);
    const childElement = diagramState.elements.find((el) => el.id === childId);
    if (!parentElement || !childElement) return;

    let validTypes = getValidRelationships(parentElement.type, childElement.type)
      .filter((type) => isStructuralRelationship(type));

    if (validTypes.length === 0) {
      validTypes = ['composition', 'aggregation'] as RelationshipType[];
    }

    setRelationshipData({
      sourceId: parentId,
      targetId: childId,
      validTypes: Array.from(new Set(validTypes)),
    });
    setShowRelationshipDialog(true);
    setTool('select');
  };

  const addElementToDiagram = (newElement: ArchiMateElement) => {
    let promptRequest: { parentId: string; childId: string } | null = null;
    let resultingDiagram: ExtendedArchiMateDiagram | null = null;

    setDiagram((current) => {
      const workingDiagram: ExtendedArchiMateDiagram = {
        ...current,
        elements: [...current.elements, newElement],
      };

      const syncResult = syncContainmentAfterMove(workingDiagram, newElement.id);
      const nextDiagram = {
        ...syncResult.diagram,
        updatedAt: Date.now(),
      };

      if (syncResult.prompts.length > 0) {
        promptRequest = syncResult.prompts[0] ?? null;
      }

      resultingDiagram = nextDiagram;
      return nextDiagram;
    });

    if (promptRequest && resultingDiagram) {
      const { parentId, childId } = promptRequest;
      if (parentId && childId) {
        promptContainmentRelationship(parentId, childId, resultingDiagram);
      }
    }
  };

  const handleCanvasDrop = (data: string, x: number, y: number, targetElementId?: string) => {
    // Check if it's JSON (existing component) or element type string
    try {
      const component = JSON.parse(data) as ArchiMateElement;
      // It's a component from library - create linked instance
      const masterId = component.masterId || component.id;

      const newElement: ArchiMateElement = {
        ...component,
        id: nanoid(),
        x,
        y,
        width: 120, // Reset to default size (not inherited)
        height: 60,
        masterId, // Link to master
        isMaster: false,
      };

      addElementToDiagram(newElement);
      setSelectedElementIds([]);

      // If dropped on another element, offer to create relationship
      if (targetElementId) {
        const sourceElement = diagram.elements.find(e => e.id === targetElementId);
        if (sourceElement) {
          const validTypes = getValidRelationships(sourceElement.type, newElement.type);
          if (validTypes.length > 0) {
            setTimeout(() => {
              setRelationshipData({
                sourceId: targetElementId,
                targetId: newElement.id,
                validTypes,
              });
              setShowRelationshipDialog(true);
            }, 100);
          }
        }
      }

      return;
    } catch {
      // It's an element type from palette
      const metadata = getElementMetadata(data as ElementType);
      if (!metadata) return;

      const newElement: ArchiMateElement = {
        id: nanoid(),
        type: data as ElementType,
        layer: metadata.layer,
        name: metadata.name,
        x,
        y,
        width: 120,
        height: 60,
      };

      addElementToDiagram(newElement);
      setSelectedElementIds([]);
    }
  };

  const determineLayer = (category?: string): Layer => {
    if (!category) return 'business';
    const normalized = category.toLowerCase();
    if (normalized.includes('strategy')) return 'strategy';
    if (normalized.includes('platform') || normalized.includes('technology')) return 'technology';
    if (normalized.includes('data') || normalized.includes('enabler')) return 'physical';
    return 'business';
  };

  const generateCapabilityGridLayout = (model: CapabilityModelResponse) => {
    const newElements: ArchiMateElement[] = [];
    const newRelationships: ArchiMateRelationship[] = [];
    const capabilityElementMap = new Map<string, string>();
    const capabilityNameMap = new Map<string, string>();
    const baseX = 180;
    const baseY = 160;
    const columnWidth = 280;
    const rowHeight = 170;
    const categoryRowCounts = new Map<string, number>();
    const categoryColumns = new Map<string, number>();

    const getColumnIndex = (category: string) => {
      if (!categoryColumns.has(category)) {
        categoryColumns.set(category, categoryColumns.size);
      }
      return categoryColumns.get(category)!;
    };

    const registerElement = (key: string | undefined, elementId: string, name: string) => {
      if (key) {
        capabilityElementMap.set(key, elementId);
      }
      capabilityNameMap.set(name.toLowerCase(), elementId);
    };

    model.capabilities.forEach((capability) => {
      const category = capability.category || 'Capabilities';
      const columnIndex = getColumnIndex(category);
      const currentRow = categoryRowCounts.get(category) ?? 0;
      const x = baseX + columnIndex * columnWidth;
      const y = baseY + currentRow * rowHeight;
      categoryRowCounts.set(category, currentRow + 1);

      const elementId = nanoid();
      registerElement(capability.id, elementId, capability.name);

      const properties: Record<string, string> = {};
      if (capability.maturity) properties.maturity = capability.maturity;
      if (capability.owner) properties.owner = capability.owner;
      if (capability.priority) properties.priority = capability.priority;
      if (capability.category) properties.category = capability.category;
      if (typeof capability.score === 'number') properties.score = capability.score.toString();

      newElements.push({
        id: elementId,
        type: 'capability',
        layer: determineLayer(capability.category),
        name: capability.name,
        documentation: capability.description,
        x,
        y,
        width: 220,
        height: 100,
        properties,
      });

      capability.children?.forEach((child, index) => {
        const childId = nanoid();
        registerElement(child.id ?? `${capability.id || capability.name}-child-${index}`, childId, child.name);
        const childX = x + (index % 2 === 0 ? -140 : 140);
        const childY = y + 130 + Math.floor(index / 2) * 110;
        const childProps: Record<string, string> = {};
        if (child.maturity) childProps.maturity = child.maturity;
        if (child.owner) childProps.owner = child.owner;
        if (child.priority) childProps.priority = child.priority;

        newElements.push({
          id: childId,
          type: 'capability',
        layer: determineLayer(capability.category),
          name: child.name,
          documentation: child.description,
          x: childX,
          y: childY,
          width: 200,
          height: 80,
          properties: childProps,
        });

        newRelationships.push({
          id: nanoid(),
          type: 'composition',
          sourceId: elementId,
          targetId: childId,
        });
      });
    });

    model.capabilities.forEach((capability) => {
      const sourceId =
        capabilityElementMap.get(capability.id || '') ??
        capabilityNameMap.get(capability.name.toLowerCase());
      if (!sourceId) return;

      capability.dependencies?.forEach((dependency) => {
        const normalized = dependency?.toLowerCase();
        if (!normalized) return;
        const targetId =
          capabilityElementMap.get(dependency) ??
          capabilityNameMap.get(normalized);
        if (!targetId || targetId === sourceId) return;

        newRelationships.push({
          id: nanoid(),
          type: 'serving',
          sourceId,
          targetId,
          name: 'Depends On',
        });
      });
    });

    return { elements: newElements, relationships: newRelationships };
  };

  const generateValueStreamLayout = (model: CapabilityModelResponse) => {
    const newElements: ArchiMateElement[] = [];
    const newRelationships: ArchiMateRelationship[] = [];
    const capabilityElementMap = new Map<string, string>();
    const baseX = 200;
    const baseY = 140;
    const columnWidth = 280;
    const childYOffset = 140;

    model.capabilities.forEach((capability, index) => {
      const x = baseX + index * columnWidth;
      const y = baseY;
      const elementId = nanoid();
      capabilityElementMap.set(capability.id || capability.name, elementId);

      const properties: Record<string, string> = {};
      if (capability.priority) properties.priority = capability.priority;
      if (capability.category) properties.category = capability.category;
      if (capability.owner) properties.owner = capability.owner;

      newElements.push({
        id: elementId,
        type: 'capability',
        layer: 'business',
        name: capability.name,
        documentation: capability.description,
        x,
        y,
        width: 240,
        height: 90,
        properties,
      });

      capability.children?.forEach((child, childIndex) => {
        const childId = nanoid();
        capabilityElementMap.set(child.id || `${capability.name}-child-${childIndex}`, childId);
        const childX = x + (childIndex % 2 === 0 ? -120 : 120);
        const childY = y + childYOffset + Math.floor(childIndex / 2) * 120;
        newElements.push({
          id: childId,
          type: 'capability',
          layer: 'application',
          name: child.name,
          documentation: child.description,
          x: childX,
          y: childY,
          width: 200,
          height: 80,
        });

        newRelationships.push({
          id: nanoid(),
          type: 'composition',
          sourceId: elementId,
          targetId: childId,
        });
      });

      if (index > 0) {
        const prevId = capabilityElementMap.get(model.capabilities[index - 1].id || model.capabilities[index - 1].name);
        if (prevId) {
          newRelationships.push({
            id: nanoid(),
            type: 'flow',
            sourceId: prevId,
            targetId: elementId,
            name: 'Journey Step',
          });
        }
      }

      capability.dependencies?.forEach((dep) => {
        const targetId = capabilityElementMap.get(dep) || capabilityElementMap.get(dep.toLowerCase());
        if (targetId && targetId !== elementId) {
          newRelationships.push({
            id: nanoid(),
            type: 'serving',
            sourceId: elementId,
            targetId,
            name: 'Depends On',
          });
        }
      });
    });

    return { elements: newElements, relationships: newRelationships };
  };

  const platformTiers = ['customer', 'core', 'platform', 'enabler'];

  const generatePlatformStackLayout = (model: CapabilityModelResponse) => {
    const newElements: ArchiMateElement[] = [];
    const newRelationships: ArchiMateRelationship[] = [];
    const capabilityElementMap = new Map<string, string>();
    const tierRowCounts = new Map<string, number>();
    const baseY = 120;
    const rowHeight = 150;
    const columnWidth = 260;

    const getTier = (category?: string) => {
      if (!category) return 'core';
      const normalized = category.toLowerCase();
      if (normalized.includes('customer') || normalized.includes('experience')) return 'customer';
      if (normalized.includes('platform') || normalized.includes('technology')) return 'platform';
      if (normalized.includes('enable') || normalized.includes('shared')) return 'enabler';
      return 'core';
    };

    platformTiers.forEach((tier) => {
      tierRowCounts.set(tier, 0);
    });

    model.capabilities.forEach((capability) => {
      const tier = getTier(capability.category);
      const currentRow = tierRowCounts.get(tier) ?? 0;
      const tierIndex = platformTiers.indexOf(tier);
      const y = baseY + (tierIndex >= 0 ? tierIndex : platformTiers.length) * rowHeight;
      const x = 200 + currentRow * columnWidth;
      tierRowCounts.set(tier, currentRow + 1);

      const elementId = nanoid();
      capabilityElementMap.set(capability.id || capability.name, elementId);

      newElements.push({
        id: elementId,
        type: 'capability',
        layer: determineLayer(capability.category),
        name: capability.name,
        documentation: capability.description,
        x,
        y,
        width: 220,
        height: 90,
        properties: {
          tier,
          priority: capability.priority || '',
        },
      });

      capability.children?.forEach((child, index) => {
        const childId = nanoid();
        capabilityElementMap.set(child.id || `${capability.name}-child-${index}`, childId);
        newElements.push({
          id: childId,
          type: 'capability',
          layer: 'application',
          name: child.name,
          documentation: child.description,
          x: x + (index % 2 === 0 ? -140 : 140),
          y: y + 110 + Math.floor(index / 2) * 110,
          width: 200,
          height: 80,
        });

        newRelationships.push({
          id: nanoid(),
          type: 'composition',
          sourceId: elementId,
          targetId: childId,
        });
      });

      capability.dependencies?.forEach((dep) => {
        const targetId = capabilityElementMap.get(dep) || capabilityElementMap.get(dep.toLowerCase());
        if (targetId && targetId !== elementId) {
          newRelationships.push({
            id: nanoid(),
            type: 'serving',
            sourceId: elementId,
            targetId,
            name: 'Supports',
          });
        }
      });
    });

    return { elements: newElements, relationships: newRelationships };
  };

  const convertCapabilityModel = (model: CapabilityModelResponse, layout: CapabilityModelLayout) => {
    switch (layout) {
      case 'value-stream':
        return generateValueStreamLayout(model);
      case 'platform-stack':
        return generatePlatformStackLayout(model);
      case 'capability-grid':
      default:
        return generateCapabilityGridLayout(model);
    }
  };

  const handleGenerateCapabilities = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCapabilityError(null);
    setCapabilityLoading(true);
    setCapabilityDraft(null);

    const focusAreas = capabilityForm.focusAreas
      .split(',')
      .map((area) => area.trim())
      .filter(Boolean);

    try {
      const response = await fetch('/api/secure/archimate/capability-model', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          industry: capabilityForm.industry,
          strategicIntent: capabilityForm.strategicIntent,
          focusAreas,
          maturityTarget: capabilityForm.maturityTarget,
          includeDependencies: capabilityForm.includeDependencies,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate capability model');
      }

      const capabilityModel: CapabilityModelResponse = data.capabilityModel;
      const imports = convertCapabilityModel(capabilityModel, capabilityForm.modelType);

      setCapabilityDraft({
        elements: imports.elements,
        relationships: imports.relationships,
        summary: capabilityModel.summary,
        type: capabilityForm.modelType,
      });
      setShowCapabilityGenerator(false);
    } catch (error: any) {
      setCapabilityError(error.message || 'Failed to generate capability model');
    } finally {
      setCapabilityLoading(false);
    }
  };

  const getContainmentParent = (diagramState: ExtendedArchiMateDiagram, child: ArchiMateElement) => {
    const candidates = diagramState.elements
      .filter((el) => el.id !== child.id && elementContains(el, child))
      .map((el) => ({ el, area: (el.width || 120) * (el.height || 60) }))
      .sort((a, b) => a.area - b.area);

    return candidates[0]?.el;
  };

  const ensureContainmentRelationship = (
    diagramState: ExtendedArchiMateDiagram,
    childId: string,
    previousParentId: string | null
  ): {
    diagram: ExtendedArchiMateDiagram;
    relationshipPrompt?: { parentId: string; childId: string };
    currentParentId: string | null;
  } => {
    const child = diagramState.elements.find((el) => el.id === childId);
    if (!child) return { diagram: diagramState, currentParentId: previousParentId };

    const parent = getContainmentParent(diagramState, child);
    const currentParentId = parent ? parent.id : null;
    const existingStructural = diagramState.relationships.find(
      (rel) => isStructuralRelationship(rel.type) && rel.targetId === childId
    );

    let relationships = diagramState.relationships;
    let changed = false;
    let relationshipPrompt: { parentId: string; childId: string } | undefined;

    const removeExisting = () => {
      const filtered = relationships.filter(
        (rel) => !(isStructuralRelationship(rel.type) && rel.targetId === childId)
      );
      if (filtered.length !== relationships.length) {
        relationships = filtered;
        changed = true;
      }
    };

    if (parent) {
      const needsPrompt =
        !existingStructural ||
        existingStructural.sourceId !== parent.id ||
        previousParentId !== parent.id;

      if (needsPrompt) {
        if (existingStructural && existingStructural.sourceId !== parent.id) {
          removeExisting();
        }
        relationshipPrompt = { parentId: parent.id, childId };
      }
    }

    if (changed) {
      diagramState = {
        ...diagramState,
        relationships,
      };
    }

    return { diagram: diagramState, relationshipPrompt, currentParentId };
  };

  const syncContainmentAfterMove = (
    diagramState: ExtendedArchiMateDiagram,
    movedElementId: string
  ): {
    diagram: ExtendedArchiMateDiagram;
    prompts: { parentId: string; childId: string }[];
  } => {
    const movedElement = diagramState.elements.find((el) => el.id === movedElementId);
    if (!movedElement) return { diagram: diagramState, prompts: [] };

    const candidateIds = new Set<string>([movedElementId]);

    diagramState.elements.forEach((el) => {
      if (el.id === movedElementId) return;
      if (elementContains(movedElement, el)) {
        candidateIds.add(el.id);
      }
    });

    let workingDiagram = diagramState;
    const prompts: { parentId: string; childId: string }[] = [];

    candidateIds.forEach((childId) => {
      const previousParentId = containmentStateRef.current.get(childId) ?? null;
      const result = ensureContainmentRelationship(workingDiagram, childId, previousParentId);
      workingDiagram = result.diagram;
      containmentStateRef.current.set(childId, result.currentParentId);
      if (result.relationshipPrompt) {
        prompts.push(result.relationshipPrompt);
      }
    });

    return { diagram: workingDiagram, prompts };
  };

  const handleUpdateElement = async (id: string, updates: Partial<ArchiMateElement>) => {
    const element = diagram.elements.find(e => e.id === id);
    let promptRequest: { parentId: string; childId: string } | null = null;
    let resultingDiagram: ExtendedArchiMateDiagram | null = null;

    setDiagram((current) => {
      const base: ExtendedArchiMateDiagram = {
        ...current,
        elements: current.elements.map(e => e.id === id ? { ...e, ...updates } : e),
      };

      const syncResult = syncContainmentAfterMove(base, id);
      const nextDiagram = {
        ...syncResult.diagram,
        updatedAt: Date.now(),
      };

      if (syncResult.prompts.length > 0) {
        promptRequest = syncResult.prompts[0] ?? null;
      }

      resultingDiagram = nextDiagram;
      return nextDiagram;
    });

    if (promptRequest && resultingDiagram) {
      const { parentId, childId } = promptRequest;
      if (parentId && childId) {
        promptContainmentRelationship(parentId, childId, resultingDiagram);
      }
    }

    // If updating name and this is linked to a master, update ALL diagrams
    if (updates.name && (element?.isMaster || element?.masterId)) {
      const masterId = element.isMaster ? id : element.masterId;

      // Load all diagrams and update instances
      const allDiagramMetas = await getAllDiagrams();

      for (const meta of allDiagramMetas) {
        if (meta.id === diagram.id) continue; // Skip current (already updated)

        const otherDiagram = await loadDiagram(meta.id);
        if (!otherDiagram) continue;

        // Check if this diagram has instances of this master
        const hasInstances = otherDiagram.elements.some(
          e => e.id === masterId || e.masterId === masterId
        );

        if (hasInstances) {
          // Update instances in this diagram (ONLY name, not size)
          const updatedOther = {
            ...otherDiagram,
            elements: otherDiagram.elements.map(e => {
              if (e.id === masterId || e.masterId === masterId) {
                // Only update name, preserve position and size
                return { ...e, name: updates.name! };
              }
              return e;
            }),
            updatedAt: Date.now(),
          };

          await saveDiagram(updatedOther);
        }
      }
    }
  };

  const handleCreateRelationship = (type: RelationshipType) => {
    if (!relationshipData) return;

    const newRelationship: ArchiMateRelationship = {
      id: nanoid(),
      type,
      sourceId: relationshipData.sourceId,
      targetId: relationshipData.targetId,
    };

    setDiagram((prev) => ({
      ...prev,
      relationships: [...prev.relationships, newRelationship],
      updatedAt: Date.now(),
    }));

    setShowRelationshipDialog(false);
    setRelationshipData(null);
    setTool('select');
  };

  const handleContextMenu = (x: number, y: number, type: 'element' | 'relationship', id: string) => {
    setContextMenu({ x, y, type, id });
  };

  const handleDoubleClick = (element: ArchiMateElement) => {
    setEditingElement(element.id);
    setEditName(element.name);
  };

  const deleteElements = (ids: string[]) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    setDiagram((current) => ({
      ...current,
      elements: current.elements.filter((e) => !idSet.has(e.id)),
      relationships: current.relationships.filter(
        (r) => !idSet.has(r.sourceId) && !idSet.has(r.targetId)
      ),
      updatedAt: Date.now(),
    }));
    setSelectedElement(null);
    setSelectedElementIds([]);
  };

  const handleDeleteElement = (id: string) => {
    deleteElements([id]);
  };

  const handleDeleteFromModel = async (id: string) => {
    const element = diagram.elements.find(e => e.id === id);
    if (!element) return;

    const masterId = element.isMaster ? id : element.masterId;

    if (!masterId) {
      // No master link, just delete this element
      handleDeleteElement(id);
      return;
    }

    // Load all diagrams and delete master + instances
    const allDiagramMetas = await getAllDiagrams();

    for (const meta of allDiagramMetas) {
      const otherDiagram = await loadDiagram(meta.id);
      if (!otherDiagram) continue;

      // Find all elements linked to this master
      const elementsToDelete = otherDiagram.elements.filter(
        e => e.id === masterId || e.masterId === masterId
      );

      if (elementsToDelete.length > 0) {
        const idsToDelete = new Set(elementsToDelete.map(e => e.id));

        const updatedDiagram = {
          ...otherDiagram,
          elements: otherDiagram.elements.filter(e => !idsToDelete.has(e.id)),
          relationships: otherDiagram.relationships.filter(
            r => !idsToDelete.has(r.sourceId) && !idsToDelete.has(r.targetId)
          ),
          updatedAt: Date.now(),
        };

        await saveDiagram(updatedDiagram);

        // If this is the current diagram, update state
        if (meta.id === diagram.id) {
          setDiagram(updatedDiagram);
          setSelectedElement(null);
          setSelectedElementIds([]);
        }
      }
    }
  };

  const handleInspectElement = (id: string) => {
    const element = diagram.elements.find(e => e.id === id);
    if (element) {
      setInspectElement(element);
    }
  };

  const handleCopyElement = (id: string) => {
    const element = diagram.elements.find(e => e.id === id);
    if (element) {
      setClipboard(element);
    }
  };

  const handleInsertCapabilityDraft = () => {
    if (!capabilityDraft) return;
    setDiagram((current) => ({
      ...current,
      elements: [...current.elements, ...capabilityDraft.elements],
      relationships: [...current.relationships, ...capabilityDraft.relationships],
      updatedAt: Date.now(),
    }));
    setSelectedElementIds([]);
    setCapabilityDraft(null);
    setLastCapabilitySummary(capabilityDraft.summary);
  };

  const handleDiscardCapabilityDraft = () => {
    setCapabilityDraft(null);
  };

  const handlePaste = () => {
    if (!clipboard) return;

    // Create new element with new ID at offset position
    const newElement: ArchiMateElement = {
      ...clipboard,
      id: nanoid(),
      x: clipboard.x + 50,
      y: clipboard.y + 50,
      name: `${clipboard.name} (copy)`,
    };

    setDiagram({
      ...diagram,
      elements: [...diagram.elements, newElement],
      updatedAt: Date.now(),
    });
    setSelectedElementIds([]);
  };

  const handleSelectComponent = (component: ArchiMateElement) => {
    // Add component instance linked to master
    const masterId = component.masterId || component.id;

    const newElement: ArchiMateElement = {
      ...component,
      id: nanoid(),
      x: 0, // Center of canvas
      y: 0,
      width: 120, // Reset to default size (not inherited)
      height: 60,
      masterId, // Link to master component for name
      isMaster: false, // This is an instance
    };

    setDiagram({
      ...diagram,
      elements: [...diagram.elements, newElement],
      updatedAt: Date.now(),
    });
    setSelectedElementIds([]);
  };

  const handleDuplicate = (id: string) => {
    const element = diagram.elements.find(e => e.id === id);
    if (!element) return;

    const newElement: ArchiMateElement = {
      ...element,
      id: nanoid(),
      x: element.x + 50,
      y: element.y + 50,
      name: `${element.name} (copy)`,
    };

    setDiagram({
      ...diagram,
      elements: [...diagram.elements, newElement],
      updatedAt: Date.now(),
    });
    setSelectedElementIds([]);
  };

  const handleDeleteRelationship = (id: string) => {
    setDiagram({
      ...diagram,
      relationships: diagram.relationships.filter(r => r.id !== id),
      updatedAt: Date.now(),
    });
  };

  const handleSelectElement = (element: ArchiMateElement | null) => {
    // Close context menu on any click
    setContextMenu(null);
    if (selectedElementIds.length > 0) {
      setSelectedElementIds([]);
    }

    // Ignore undefined/null clicks in connect mode
    if (!element && tool === 'connect') {
      return;
    }


    if (tool === 'connect' && element) {
      if (!pendingConnection) {
        // First click - set source
        setPendingConnection(element.id);
      } else if (pendingConnection !== element.id) {
        // Second click - show relationship type selection

        const sourceElement = diagram.elements.find(e => e.id === pendingConnection);
        const targetElement = element;

        if (sourceElement && targetElement) {
          const validTypes = getValidRelationships(sourceElement.type, targetElement.type);
          setRelationshipData({
            sourceId: pendingConnection,
            targetId: element.id,
            validTypes,
          });
          setShowRelationshipDialog(true);
        }

        setPendingConnection(null);
      }
    } else {
      setSelectedElement(element);
      setPendingConnection(null);
    }
  };

  useEffect(() => {
    setMounted(true);

    // Initialize: Save current diagram to IndexedDB on first load
    const initializeDiagram = async () => {
      // Save current diagram to IndexedDB
      await saveDiagram(diagram);
    };

    initializeDiagram();

    // Keyboard shortcuts
    const handleKeyboard = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }

      // Mode switching (single key, no modifiers)
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        // V - Select mode
        if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          setTool('select');
          return;
        }

        // C - Connect mode
        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          setTool('connect');
          setPendingConnection(null);
          return;
        }

        // B - Browse diagrams
        if (e.key === 'b' || e.key === 'B') {
          e.preventDefault();
          setShowBrowser(true);
          return;
        }

        // L - Component library
        if (e.key === 'l' || e.key === 'L') {
          e.preventDefault();
          setShowComponentLibrary(true);
          return;
        }

        // ? - Keyboard shortcuts
        if (e.key === '?') {
          e.preventDefault();
          setOpenPanel('keyboard');
          return;
        }
      }

      // Cmd/Ctrl + F - Search (Query Bar)
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setQueryBarOpen(true);
        return;
      }

      // Select All (Ctrl/Cmd + A)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedElement(null);
        setSelectedElementIds(diagram.elements.map((el) => el.id));
        setTool('select');
        setPendingConnection(null);
        return;
      }

      // Ctrl/Cmd + C - Copy selected element
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedElement) {
          e.preventDefault();
          handleCopyElement(selectedElement.id);
          return;
        }
        if (selectedElementIds.length > 0) {
          e.preventDefault();
          const first = diagram.elements.find((el) => el.id === selectedElementIds[0]);
          if (first) {
            handleCopyElement(first.id);
          }
          return;
        }
      }

      // Ctrl/Cmd + V - Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) {
        e.preventDefault();
        handlePaste();
        return;
      }

      // Ctrl/Cmd + D - Duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedElement) {
        e.preventDefault();
        handleDuplicate(selectedElement.id);
        return;
      }

      // Ctrl/Cmd + E - Export PNG
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        handleExportPNG();
        return;
      }

      // Ctrl/Cmd + S - Save (already auto-saves, but provides feedback)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveDiagram(diagram);
        return;
      }

      // Delete key - Delete selected element
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElementIds.length > 0) {
          e.preventDefault();
          deleteElements(selectedElementIds);
          return;
        }
        if (selectedElement) {
          e.preventDefault();
          handleDeleteElement(selectedElement.id);
          return;
        }
      }

      // Escape - Close overlays / deselect / cancel
      if (e.key === 'Escape') {
        // Close dialogs/panels first
        if (queryBarOpen) {
          setQueryBarOpen(false);
          return;
        }
        if (openPanel) {
          setOpenPanel(null);
          return;
        }
        if (showBrowser) {
          setShowBrowser(false);
          return;
        }
        if (showComponentLibrary && !componentLibraryDocked) {
          setShowComponentLibrary(false);
          return;
        }
        if (showRelationshipDialog) {
          setShowRelationshipDialog(false);
          setRelationshipData(null);
          return;
        }
        if (editingElement) {
          setEditingElement(null);
          return;
        }
        if (contextMenu) {
          setContextMenu(null);
          return;
        }
        if (selectedElementIds.length > 0) {
          setSelectedElementIds([]);
          setSelectedElement(null);
          return;
        }

        // Then deselect / cancel mode
        setSelectedElement(null);
        setTool('select');
        setPendingConnection(null);
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [selectedElement, clipboard, selectedElementIds, diagram.elements]);

  // Auto-save to IndexedDB on changes (debounced)
  useEffect(() => {
    if (mounted) {
      const timeoutId = setTimeout(() => {
        saveDiagram(diagram);
      }, 1000); // Save 1 second after last change

      return () => clearTimeout(timeoutId);
    }
  }, [diagram, mounted]);

  useEffect(() => {
    const containerMap = new Map<string, string | null>();
    diagram.elements.forEach((el) => {
      const parent = getContainmentParent(diagram, el);
      containerMap.set(el.id, parent ? parent.id : null);
    });
    containmentStateRef.current = containerMap;
  }, [diagram.elements, diagram.relationships]);

  const layers: Layer[] = ['strategy', 'business', 'application', 'technology', 'physical', 'implementation', 'motivation'];

  if (!mounted) {
    return (
      <div className="h-screen flex items-center justify-center bg-white dark:bg-background">
        <div className="text-foreground">Loading ArchiMate Editor...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-foreground">ArchiMate Editor</h1>
          <WorkspaceSelector />
          <button
            onClick={() => setShowWorkspaceManager(true)}
            className="px-3 py-1.5 bg-background/50 hover:bg-background/70 text-foreground text-sm rounded border border-border transition-colors"
            title="Manage workspaces"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          <input
            type="text"
            value={diagram.name}
            onChange={(e) => setDiagram({ ...diagram, name: e.target.value, updatedAt: Date.now() })}
            className="px-3 py-1 bg-surface border border-border rounded text-foreground text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTool('select')}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              tool === 'select'
                ? 'bg-accent text-white'
                : 'bg-surface text-foreground hover:bg-accent-muted'
            }`}
          >
            Select
          </button>
          <button
            onClick={() => {
              setTool('connect');
              setPendingConnection(null);
            }}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              tool === 'connect'
                ? 'bg-accent text-white'
                : 'bg-surface text-foreground hover:bg-accent-muted'
            }`}
          >
            Connect
          </button>
          <div className="border-l border-border h-6 mx-2"></div>
          {clipboard && (
            <button
              onClick={handlePaste}
              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
            >
              Paste ({clipboard.name})
            </button>
          )}
          <button
            onClick={handleExportXML}
            className="px-3 py-1.5 bg-surface text-foreground text-sm rounded hover:bg-accent-muted transition-colors"
          >
            Export XML
          </button>
          <button
            onClick={handleExportPNG}
            className="px-3 py-1.5 bg-surface text-foreground text-sm rounded hover:bg-accent-muted transition-colors"
          >
            Export PNG
          </button>
          <button
            onClick={() => setShowBrowser(true)}
            className="px-3 py-1.5 bg-surface text-foreground text-sm rounded hover:bg-accent-muted transition-colors"
          >
            Browse Diagrams
          </button>
          <button
            onClick={() => setShowComponentLibrary(true)}
            className="px-3 py-1.5 bg-surface text-foreground text-sm rounded hover:bg-accent-muted transition-colors"
          >
            Component Library
          </button>

          <div className="border-l border-border h-6 mx-2"></div>

          {/* Advanced Features Dropdown */}
          <div className="relative group">
            <button
              className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              Advanced Features ▾
              {openPanel && (
                <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              )}
            </button>
            <div className="absolute right-0 top-full mt-1 bg-background border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-64 z-50 max-h-[80vh] overflow-y-auto">
              <button onClick={() => setOpenPanel('adr')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between group/item">
                <span>📝 ADR (Architecture Decisions)</span>
                {(diagram).adrs?.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-accent-muted text-accent text-xs rounded">{(diagram).adrs.length}</span>
                )}
              </button>
              <button onClick={() => setOpenPanel('cost')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>💰 Cost & TCO</span>
                {Object.keys((diagram).elementMetadata || {}).some(id => (diagram).elementMetadata[id]?.cost) && (
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                )}
              </button>
              <button onClick={() => setOpenPanel('security')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>🔒 Security & Compliance</span>
                {Object.keys((diagram).elementMetadata || {}).some(id => (diagram).elementMetadata[id]?.security) && (
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                )}
              </button>
              <button onClick={() => setOpenPanel('debt')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>⚠️ Technical Debt</span>
                {Object.keys((diagram).elementMetadata || {}).some(id => (diagram).elementMetadata[id]?.debt) && (
                  <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                )}
              </button>
              <button onClick={() => setOpenPanel('validation')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>✓ Validation Rules</span>
                {(diagram).violations?.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">{(diagram).violations.length}</span>
                )}
              </button>
              <button onClick={() => setOpenPanel('performance')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>⚡ Performance & SLOs</span>
                {Object.keys((diagram).elementMetadata || {}).some(id => (diagram).elementMetadata[id]?.performance) && (
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                )}
              </button>
              <button onClick={() => setOpenPanel('team')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>👥 Team Ownership</span>
                {(diagram).teams?.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">{(diagram).teams.length}</span>
                )}
              </button>
              <button onClick={() => setOpenPanel('changelog')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>📅 Change History</span>
                {(diagram).changelog?.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">{(diagram).changelog.length}</span>
                )}
              </button>
              <button onClick={() => setOpenPanel('scenario')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>🔀 Scenarios</span>
                {(diagram).scenarios?.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">{(diagram).scenarios.length}</span>
                )}
              </button>
              <button onClick={() => setOpenPanel('migration')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>🚀 Migration Planning</span>
                {(diagram).migrationPlan && (
                  <span className="w-2 h-2 bg-accent rounded-full" />
                )}
              </button>
              <button onClick={() => setOpenPanel('stakeholder')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors flex items-center justify-between">
                <span>📊 Stakeholder Views</span>
                {(diagram).stakeholderViews?.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded">{(diagram).stakeholderViews.length}</span>
                )}
              </button>
              <button onClick={() => setOpenPanel('infrastructure')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors">
                <span>☁️ Infrastructure Import</span>
              </button>
              <div className="border-t border-border-muted my-1" />
              <div className="px-4 py-1 text-xs text-foreground-muted font-medium">Generators & Visualizations</div>
              <button onClick={() => setShowCapabilityGenerator(true)} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors">
                <span>🎯 Generate Capability Model</span>
              </button>
              <button onClick={() => setOpenPanel('view-generator')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors">
                <span>🔍 View Generator</span>
              </button>
              <button onClick={() => setOpenPanel('sequence')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-cream/10 transition-colors">
                <span>📈 Sequence Diagrams</span>
              </button>
            </div>
          </div>

          <button
            onClick={handleNewDiagram}
            className="px-3 py-1.5 bg-accent text-white text-sm rounded hover:bg-accent-hover transition-colors"
          >
            New Diagram
          </button>
        </div>
      </div>
      {lastCapabilitySummary && (
        <div className="px-4 py-2 bg-green-950 text-green-200 text-sm border-b border-green-800">
          <strong>Last capability model:</strong> {lastCapabilitySummary}
        </div>
      )}
      {capabilityDraft && (
        <div className="px-4 py-3 bg-surface border-b border-border flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-sm text-accent uppercase tracking-wide font-semibold">
              Draft ready ({CAPABILITY_SCENARIOS.find((s) => s.value === capabilityDraft.type)?.label || capabilityDraft.type})
            </div>
            <div className="text-foreground text-base">{capabilityDraft.summary}</div>
            <div className="text-foreground-tertiary text-xs">
              {capabilityDraft.elements.length} elements · {capabilityDraft.relationships.length} relationships (not yet inserted)
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDiscardCapabilityDraft}
              className="px-3 py-1.5 text-sm rounded bg-background text-foreground hover:bg-accent-muted transition-colors"
            >
              Discard Draft
            </button>
            <button
              onClick={handleInsertCapabilityDraft}
              className="px-3 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              Insert into Diagram
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Docked Component Library */}
        {componentLibraryDocked && (
          <ComponentLibrary
            onSelectComponent={handleSelectComponent}
            onClose={() => {
              setComponentLibraryDocked(false);
              setShowComponentLibrary(false);
            }}
            docked={true}
            onToggleDock={() => {
              setComponentLibraryDocked(false);
              setShowComponentLibrary(true);
            }}
            onRenameComponent={(element, newName) => {
              handleUpdateElement(element.id, { name: newName });
            }}
            onDeleteComponent={(element) => {
              const masterId = element.isMaster ? element.id : element.masterId;
              if (masterId) {
                handleDeleteFromModel(masterId);
              } else {
                handleDeleteElement(element.id);
              }
            }}
            onInspectComponent={(element) => {
              setInspectElement(element);
            }}
            onLoadDiagram={handleLoadDiagram}
            onRenameDiagram={handleRenameDiagram}
            onDeleteDiagram={handleDeleteDiagram}
          />
        )}

        {/* Canvas */}
        <div className="flex-1 relative bg-background">
          <ArchiMateCanvas
            elements={diagram.elements}
            relationships={diagram.relationships}
            selectedElement={selectedElement}
            selectedElementIds={selectedElementIds}
            pendingConnection={pendingConnection}
            tool={tool}
            onSelectElement={handleSelectElement}
            onUpdateElement={handleUpdateElement}
            onCanvasDrop={handleCanvasDrop}
            onContextMenu={handleContextMenu}
            onDoubleClick={handleDoubleClick}
            activeHeatmap={activeHeatmap}
            elementMetadata={(diagram).elementMetadata || {}}
          />
        </div>

        {/* Right Sidebar - Element Palette */}
        <div className="w-80 border-l border-border bg-surface overflow-y-auto">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-foreground mb-4">Element Palette</h2>

            {/* Layer Tabs */}
            <div className="flex flex-wrap gap-1 mb-4">
              {layers.map((layer) => (
                <button
                  key={layer}
                  onClick={() => setSelectedLayer(layer)}
                  className={`px-2 py-1 text-xs rounded capitalize transition-colors ${
                    selectedLayer === layer
                      ? 'bg-accent text-white'
                      : 'bg-background text-foreground-secondary hover:bg-background/80'
                  }`}
                >
                  {layer}
                </button>
              ))}
            </div>

            {/* Elements for Selected Layer */}
            <div className="space-y-2">
              {getElementsByLayer(selectedLayer).map((element) => (
                <div
                  key={element.type}
                  className="group p-3 rounded border-2 border-transparent hover:border-accent transition-all cursor-grab active:cursor-grabbing"
                  style={{ backgroundColor: element.color }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('elementType', element.type);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                >
                  <div className="flex items-center gap-3">
                    {/* SVG Icon */}
                    <div className="w-5 h-5 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" className="text-gray-800">
                        <PaletteIcon type={element.type} size={16} />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">{element.name}</div>
                      <div className="text-xs text-gray-600 capitalize">{element.shape}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap Indicator */}
      {activeHeatmap && (
        <div className="fixed bottom-20 left-4 bg-background/95 backdrop-blur border-2 border-accent rounded-lg p-4 shadow-xl z-40 animate-in slide-in-from-left">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-3 h-3 bg-accent rounded-full animate-pulse" />
            <span className="text-sm font-medium text-foreground capitalize">
              {activeHeatmap} Heatmap Active
            </span>
            <button
              onClick={() => setActiveHeatmap(null)}
              className="ml-2 text-foreground-tertiary hover:text-foreground"
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-green-500 rounded" />
              <span className="text-foreground-secondary">Low</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-yellow-500 rounded" />
              <span className="text-foreground-secondary">Medium</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-red-500 rounded" />
              <span className="text-foreground-secondary">High</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-border bg-background px-4 py-2 flex items-center justify-between text-xs text-foreground-tertiary">
        <div>
          Elements: {diagram.elements.length} | Relationships: {diagram.relationships.length}
          {clipboard && <span className="ml-4 text-green-400">📋 Clipboard: {clipboard.name}</span>}
          {activeHeatmap && (
            <span className="ml-4 text-accent capitalize">
              🔥 {activeHeatmap} Heatmap Active
            </span>
          )}
          {tool === 'connect' && pendingConnection && (
            <span className="ml-4 text-accent">→ Click target element to complete connection</span>
          )}
          {tool === 'connect' && !pendingConnection && (
            <span className="ml-4 text-accent">Click source element to start</span>
          )}
        </div>
        <div className="text-xs flex items-center gap-4">
          <span>ArchiMate 3.2</span>
          <span className="text-foreground-muted">V: Select | C: Connect | B: Browse | L: Library</span>
          <span className="text-purple-400">Advanced Features Menu ▾</span>
          <span className="text-foreground-muted">Ctrl+F: Search | ?: Shortcuts</span>
        </div>
      </div>

      {/* Diagram Browser */}
      {showBrowser && (
        <DiagramBrowser
          currentDiagramId={diagram.id}
          onSelectDiagram={handleLoadDiagram}
          onNewDiagram={handleNewDiagram}
          onClose={() => setShowBrowser(false)}
        />
      )}

      {/* Component Library (Modal) */}
      {showComponentLibrary && !componentLibraryDocked && (
        <ComponentLibrary
          onSelectComponent={handleSelectComponent}
          onClose={() => setShowComponentLibrary(false)}
          docked={false}
          onToggleDock={() => {
            setShowComponentLibrary(false);
            setComponentLibraryDocked(true);
          }}
          onRenameComponent={(element, newName) => {
            handleUpdateElement(element.id, { name: newName });
          }}
          onDeleteComponent={(element) => {
            const masterId = element.isMaster ? element.id : element.masterId;
            if (masterId) {
              handleDeleteFromModel(masterId);
            } else {
              handleDeleteElement(element.id);
            }
          }}
          onInspectComponent={(element) => {
            setInspectElement(element);
          }}
          onLoadDiagram={handleLoadDiagram}
          onRenameDiagram={handleRenameDiagram}
          onDeleteDiagram={handleDeleteDiagram}
        />
      )}

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={
            contextMenu.type === 'element'
              ? [
                  {
                    label: 'Inspect Connections',
                    icon: '🔍',
                    onClick: () => handleInspectElement(contextMenu.id),
                  },
                  {
                    label: 'Rename',
                    icon: '✎',
                    onClick: () => {
                      const element = diagram.elements.find(e => e.id === contextMenu.id);
                      if (element) handleDoubleClick(element);
                    },
                  },
                  {
                    label: 'Duplicate',
                    icon: '⎘',
                    onClick: () => handleDuplicate(contextMenu.id),
                  },
                  {
                    label: 'Copy',
                    icon: '⧉',
                    onClick: () => handleCopyElement(contextMenu.id),
                  },
                  {
                    label: 'Link to Command Center',
                    icon: '🔗',
                    onClick: () => {
                      const element = diagram.elements.find(e => e.id === contextMenu.id);
                      if (element) setLinkToCommandCenterElement(element);
                    },
                  },
                  {
                    label: 'Delete from Diagram',
                    icon: '✗',
                    onClick: () => handleDeleteElement(contextMenu.id),
                    danger: true,
                  },
                  {
                    label: 'Delete from Model',
                    icon: '⚠',
                    onClick: () => handleDeleteFromModel(contextMenu.id),
                    danger: true,
                  },
                ]
              : [
                  {
                    label: 'Delete Relationship',
                    icon: '✗',
                    onClick: () => handleDeleteRelationship(contextMenu.id),
                    danger: true,
                  },
                ]
          }
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Inline Name Editor */}
      {editingElement && (() => {
        const element = diagram.elements.find(e => e.id === editingElement);
        if (!element) return null;

        return (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-background border-2 border-accent rounded-lg p-6">
              <h3 className="text-lg font-bold text-foreground mb-4">Edit Element Name</h3>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editName.trim()) {
                    handleUpdateElement(editingElement, { name: editName.trim() });
                    setEditingElement(null);
                  } else if (e.key === 'Escape') {
                    setEditingElement(null);
                  }
                }}
                autoFocus
                className="w-full px-4 py-2 bg-surface border border-border rounded text-foreground mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (editName.trim()) {
                      handleUpdateElement(editingElement, { name: editName.trim() });
                      setEditingElement(null);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-accent text-white rounded hover:bg-accent-hover"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingElement(null)}
                  className="px-4 py-2 bg-surface text-foreground rounded hover:bg-accent-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Capability Generator Modal */}
      {showCapabilityGenerator && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-background border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Generate Capability Model</h2>
                <p className="text-sm text-foreground-tertiary">
                  Uses the Cloudflare AI Gateway (OpenAI) to seed capabilities into your diagram.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowCapabilityGenerator(false);
                  setCapabilityError(null);
                }}
                className="text-foreground-tertiary hover:text-foreground text-xl leading-none"
                type="button"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleGenerateCapabilities} className="px-6 py-4 space-y-4">
              <div>
                <label className="text-sm text-foreground-secondary block mb-1">Industry</label>
                <input
                  type="text"
                  value={capabilityForm.industry}
                  onChange={(e) => setCapabilityForm((prev) => ({ ...prev, industry: e.target.value }))}
                  className="w-full px-3 py-2 bg-surface border border-border rounded text-foreground"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-foreground-secondary block mb-1">Model Type</label>
                <select
                  value={capabilityForm.modelType}
                  onChange={(e) =>
                    setCapabilityForm((prev) => ({
                      ...prev,
                      modelType: e.target.value as 'capability-grid' | 'value-stream' | 'platform-stack',
                    }))
                  }
                  className="w-full px-3 py-2 bg-surface border border-border rounded text-foreground text-sm"
                >
                  {CAPABILITY_SCENARIOS.map((scenario) => (
                    <option key={scenario.value} value={scenario.value}>
                      {scenario.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-foreground-tertiary mt-1">
                  {CAPABILITY_SCENARIOS.find((s) => s.value === capabilityForm.modelType)?.description}
                </p>
              </div>
              <div>
                <label className="text-sm text-foreground-secondary block mb-1">Strategic Intent</label>
                <textarea
                  value={capabilityForm.strategicIntent}
                  onChange={(e) => setCapabilityForm((prev) => ({ ...prev, strategicIntent: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 bg-surface border border-border rounded text-foreground"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-foreground-secondary block mb-1">Focus Areas (comma separated)</label>
                <input
                  type="text"
                  value={capabilityForm.focusAreas}
                  onChange={(e) => setCapabilityForm((prev) => ({ ...prev, focusAreas: e.target.value }))}
                  className="w-full px-3 py-2 bg-surface border border-border rounded text-foreground"
                />
              </div>
              <div>
                <label className="text-sm text-foreground-secondary block mb-1">Maturity Target</label>
                <textarea
                  value={capabilityForm.maturityTarget}
                  onChange={(e) => setCapabilityForm((prev) => ({ ...prev, maturityTarget: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 bg-surface border border-border rounded text-foreground"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground-secondary">
                <input
                  type="checkbox"
                  checked={capabilityForm.includeDependencies}
                  onChange={(e) =>
                    setCapabilityForm((prev) => ({ ...prev, includeDependencies: e.target.checked }))
                  }
                  className="accent-orange"
                />
                Include dependency mapping between generated capabilities
              </label>
              {capabilityError && (
                <div className="text-sm text-red-400 bg-red-950/60 border border-red-700 rounded px-3 py-2">
                  {capabilityError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border-muted">
                <button
                  type="button"
                  onClick={() => {
                    setShowCapabilityGenerator(false);
                    setCapabilityError(null);
                  }}
                  className="px-4 py-2 bg-surface text-foreground rounded hover:bg-accent-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={capabilityLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {capabilityLoading ? 'Generating…' : 'Generate & Insert'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Relationship Type Selection Dialog */}
      {showRelationshipDialog && relationshipData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border-2 border-accent rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-foreground mb-4">Select Relationship Type</h3>
            <p className="text-sm text-foreground-secondary mb-4">
              {relationshipData.validTypes.length} valid relationship{relationshipData.validTypes.length !== 1 ? 's' : ''} available
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
              {relationshipData.validTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => handleCreateRelationship(type)}
                  className="w-full px-4 py-2 bg-surface text-foreground text-left rounded hover:bg-accent-muted transition-colors capitalize"
                >
                  {type.replace(/-/g, ' ')}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setShowRelationshipDialog(false);
                setRelationshipData(null);
                setTool('select');
              }}
              className="w-full px-4 py-2 bg-surface text-foreground rounded hover:bg-accent-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Inspect Modal */}
      {inspectElement && (
        <InspectModal
          element={inspectElement}
          diagram={diagram}
          onClose={() => setInspectElement(null)}
        />
      )}

      {/* Link to Command Center Modal */}
      {linkToCommandCenterElement && (
        <LinkToEntityModal
          element={linkToCommandCenterElement}
          onClose={() => setLinkToCommandCenterElement(null)}
          onLinked={(entityId, mappingId) => {
            console.log('Element linked to entity:', { entityId, mappingId });
            setLinkToCommandCenterElement(null);
          }}
        />
      )}

      {/* Extended Feature Panels */}
      {openPanel === 'adr' && (
        <ADRPanel
          diagram={diagram}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {openPanel === 'cost' && (
        <CostPanel
          diagram={diagram}
          selectedElementId={selectedElement?.id}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onToggleHeatmap={(enabled) => setActiveHeatmap(enabled ? 'cost' : null)}
        />
      )}

      {openPanel === 'security' && (
        <SecurityPanel
          diagram={diagram}
          selectedElementId={selectedElement?.id}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onToggleHeatmap={(enabled) => setActiveHeatmap(enabled ? 'security' : null)}
        />
      )}

      {openPanel === 'debt' && (
        <DebtPanel
          diagram={diagram}
          selectedElementId={selectedElement?.id}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onToggleHeatmap={(enabled) => setActiveHeatmap(enabled ? 'debt' : null)}
        />
      )}

      {openPanel === 'validation' && (
        <ValidationPanel
          diagram={diagram}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {openPanel === 'performance' && (
        <PerformancePanel
          diagram={diagram}
          selectedElementId={selectedElement?.id}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onToggleHeatmap={(enabled) => setActiveHeatmap(enabled ? 'performance' : null)}
        />
      )}

      {openPanel === 'team' && (
        <TeamPanel
          diagram={diagram}
          selectedElementId={selectedElement?.id}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onSelectElement={(id) => {
            const element = diagram.elements.find(el => el.id === id);
            if (element) setSelectedElement(element);
          }}
        />
      )}

      {openPanel === 'changelog' && (
        <ChangelogPanel
          diagram={diagram}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onSelectElement={(id) => {
            const element = diagram.elements.find(el => el.id === id);
            if (element) setSelectedElement(element);
          }}
        />
      )}

      {openPanel === 'scenario' && (
        <ScenarioPanel
          diagram={diagram}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onSelectElement={(id) => {
            const element = diagram.elements.find(el => el.id === id);
            if (element) setSelectedElement(element);
          }}
        />
      )}

      {openPanel === 'migration' && (
        <MigrationPanel
          diagram={diagram}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onSelectElement={(id) => {
            const element = diagram.elements.find(el => el.id === id);
            if (element) setSelectedElement(element);
          }}
        />
      )}

      {openPanel === 'stakeholder' && (
        <StakeholderPanel
          diagram={diagram}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
          onSelectElement={(id) => {
            const element = diagram.elements.find(el => el.id === id);
            if (element) setSelectedElement(element);
          }}
        />
      )}

      {openPanel === 'infrastructure' && (
        <InfrastructurePanel
          diagram={diagram}
          onUpdate={(updated) => setDiagram(updated)}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {openPanel === 'view-generator' && (
        <ViewGeneratorPanel
          diagram={diagram}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {openPanel === 'sequence' && (
        <SequenceDiagramPanel
          diagram={diagram}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {/* Query Bar */}
      <QueryBar
        isOpen={queryBarOpen}
        diagram={diagram}
        onClose={() => setQueryBarOpen(false)}
        onResults={setQueryResults}
        onSelectElement={(id) => {
          const element = diagram.elements.find(el => el.id === id);
          if (element) setSelectedElement(element);
          setQueryBarOpen(false);
        }}
      />

      {/* MiniMap */}
      <MiniMap
        diagram={diagram}
        viewport={viewport}
        onViewportChange={(x, y) => setViewport({ ...viewport, x, y })}
        isVisible={minimapVisible}
        onToggle={() => setMinimapVisible(!minimapVisible)}
      />

      {/* Workspace Manager */}
      <WorkspaceManager
        isOpen={showWorkspaceManager}
        onClose={() => setShowWorkspaceManager(false)}
      />
    </div>
  );
}
